import { useAuth } from "@/hooks/useAuthV2";

interface PermissionGuardProps {
  children: React.ReactNode;
  permission?: string;
  permissions?: string[];
  requireAll?: boolean;
  fallback?: React.ReactNode;
}

/**
 * Компонент для условного рендеринга на основе разрешений пользователя.
 * 
 * Использование:
 * - <PermissionGuard permission="admin.access">...</PermissionGuard>
 * - <PermissionGuard permissions={["admin.access", "users.manage"]} requireAll>...</PermissionGuard>
 * - <PermissionGuard permissions={["admin.access", "users.manage"]} requireAll={false}>...</PermissionGuard>
 */
export function PermissionGuard({ 
  children, 
  permission,
  permissions,
  requireAll = false,
  fallback = null 
}: PermissionGuardProps) {
  const { hasPermission, hasAnyPermission, hasAllPermissions, isLoading } = useAuth();

  // Если загружаются данные, не показываем ничего (или можно показать fallback)
  if (isLoading) {
    return <>{fallback}</>;
  }

  // Если указано одно разрешение
  if (permission) {
    if (hasPermission(permission)) {
      return <>{children}</>;
    }
    return <>{fallback}</>;
  }

  // Если указан массив разрешений
  if (permissions && permissions.length > 0) {
    const hasAccess = requireAll 
      ? hasAllPermissions(permissions)
      : hasAnyPermission(permissions);
    
    if (hasAccess) {
      return <>{children}</>;
    }
    return <>{fallback}</>;
  }

  // Если не указаны разрешения, показываем контент (для обратной совместимости)
  return <>{children}</>;
}
